import {
    AfterViewInit,
    Component,
    ComponentRef,
    ElementRef,
    Inject,
    Injector,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef,
} from '@angular/material/dialog';

export interface AppDialogWrapperConfig {
    title?: string;
    component: Type<any>;
    data?: any;
    width?: string;
    height?: string;
    minHeight?: string;
    maxWidth?: string;
    maxHeight?: string;
    disableClose?: boolean;
    hideHeader?: boolean;
}

@Component({
    selector: 'app-dialog-wrapper',
    styleUrls: ['./app-dialog-wrapper.component.scss'],
    templateUrl: './app-dialog-wrapper.component.html',
})
export class AppDialogWrapperComponent implements AfterViewInit {
    @ViewChild('contentContainer', { read: ViewContainerRef })
    contentContainer: ViewContainerRef;

    title: string;
    component: Type<any>;
    componentData: any;
    hideHeader: boolean;
    minHeight: string;

    private componentRef: ComponentRef<any>;

    constructor(
        public dialogRef: MatDialogRef<AppDialogWrapperComponent>,
        @Inject(MAT_DIALOG_DATA) public config: AppDialogWrapperConfig,
        private injector: Injector,
        public elementRef: ElementRef
    ) {
        this.title = config.title;
        this.component = config.component;
        this.componentData = config.data;
        this.hideHeader = config.hideHeader ?? false;
        this.minHeight = config.minHeight;
    }

    /**
     * Get the component instance of the dynamically loaded component
     */
    getComponentInstance(): any {
        return this.componentRef?.instance;
    }

    ngAfterViewInit(): void {
        // Apply minHeight to host element if specified
        if (this.minHeight && this.elementRef?.nativeElement) {
            this.elementRef.nativeElement.style.minHeight = this.minHeight;
        }
        this.loadComponent();
    }

    private loadComponent(): void {
        if (this.component && this.contentContainer) {
            this.contentContainer.clear();
            
            // Create injector that provides MAT_DIALOG_DATA and MatDialogRef
            // This allows components using @Inject(MAT_DIALOG_DATA) to work correctly
            const componentInjector = Injector.create({
                providers: [
                    { provide: MAT_DIALOG_DATA, useValue: this.componentData },
                    { provide: MatDialogRef, useValue: this.dialogRef }
                ],
                parent: this.injector
            });

            this.componentRef = this.contentContainer.createComponent(this.component, {
                injector: componentInjector
            });

            // Pass data to the dynamically loaded component (for backwards compatibility)
            // Components that don't use @Inject(MAT_DIALOG_DATA) can still receive data via property assignment
            if (this.componentRef.instance) {
                // Pass the dialog ref so child component can close the dialog
                // Always assign so components that declare dialogRef via DI or as a plain property both receive it
                (this.componentRef.instance as any).dialogRef = this.dialogRef;

                // Subscribe to generic dialogAction event if it exists
                if (this.componentRef.instance.dialogAction) {
                    this.componentRef.instance.dialogAction.subscribe((event: { action: string; data?: any }) => {
                        if (event.action === 'close') {
                            // For close action, pass the data directly (or undefined if no data)
                            this.dialogRef.close(event.data);
                        } else {
                            // For other actions, format as { mode: action, ...data } for parent component compatibility
                            const result: any = { mode: event.action };
                            if (event.data && typeof event.data === 'object') {
                                Object.assign(result, event.data);
                            }
                            this.dialogRef.close(result);
                        }
                    });
                }

                // Subscribe to cancelDialog event if it exists - closes dialog without data
                if (this.componentRef.instance.cancelDialog) {
                    this.componentRef.instance.cancelDialog.subscribe(() => {
                        this.dialogRef.close();
                    });
                }

                // Subscribe to closeDialog event if it exists - closes dialog with data
                if (this.componentRef.instance.closeDialog) {
                    this.componentRef.instance.closeDialog.subscribe((data: any) => {
                        this.dialogRef.close(data);
                    });
                }

                if (this.componentData) {
                    // If component expects data via @Input or property (not via @Inject)
                    // Always overwrite to ensure correct data is set (component may have set default data in constructor)
                    if ('data' in this.componentRef.instance) {
                        this.componentRef.instance.data = this.componentData;
                    }
                    // Also try to assign properties directly (for backwards compatibility)
                    if (typeof this.componentData === 'object' && !Array.isArray(this.componentData)) {
                        Object.keys(this.componentData).forEach((key) => {
                            if (key in this.componentRef.instance && key !== 'data') {
                                this.componentRef.instance[key] = this.componentData[key];
                            }
                        });
                    }
                }
            }
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }
}

